import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const MobilePreviewModal = ({ isOpen, onClose, artistData }) => {
  const [currentScreen, setCurrentScreen] = useState('splash');

  if (!isOpen) return null;

  const screens = [
    { id: 'splash', name: 'Splash', icon: 'Smartphone' },
    { id: 'home', name: 'Home', icon: 'Home' },
    { id: 'about', name: 'About', icon: 'User' },
    { id: 'calendar', name: 'Calendar', icon: 'Calendar' },
    { id: 'work', name: 'Work', icon: 'Briefcase' },
    { id: 'shop', name: 'Shop', icon: 'ShoppingCart' }
  ];

  const renderScreen = () => {
    switch (currentScreen) {
      case 'splash':
        return (
          <div className="w-full h-full bg-gradient-to-b from-primary to-accent flex items-center justify-center">
            {artistData?.promotion?.splashImage ? (
              <Image
                src={artistData?.promotion?.splashImage}
                alt="Splash screen"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-white">
                <Icon name="Palette" size={48} className="mx-auto mb-4" />
                <h1 className="text-2xl font-bold">Sarah Mitchell</h1>
                <p className="text-sm opacity-80">Visual Artist</p>
              </div>
            )}
          </div>
        );
      
      case 'home':
        return (
          <div className="w-full h-full bg-surface relative">
            {artistData?.promotion?.homeImage && (
              <Image
                src={artistData?.promotion?.homeImage}
                alt="Home background"
                className="w-full h-32 object-cover"
              />
            )}
            <div className="p-4">
              <div className="flex items-center space-x-3 mb-4">
                {artistData?.promotion?.logo ? (
                  <Image
                    src={artistData?.promotion?.logo}
                    alt="Artist logo"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                )}
                <div>
                  <h2 className="font-bold text-text-primary">Sarah Mitchell</h2>
                  <p className="text-sm text-text-secondary">Visual Artist</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted p-3 rounded-lg">
                  <Icon name="Calendar" size={20} className="text-accent mb-2" />
                  <p className="text-xs font-medium">Upcoming Events</p>
                  <p className="text-xs text-text-secondary">3 scheduled</p>
                </div>
                <div className="bg-muted p-3 rounded-lg">
                  <Icon name="Image" size={20} className="text-success mb-2" />
                  <p className="text-xs font-medium">Latest Work</p>
                  <p className="text-xs text-text-secondary">Digital Series</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'about':
        return (
          <div className="w-full h-full bg-surface overflow-y-auto">
            {artistData?.about?.headerImage && (
              <Image
                src={artistData?.about?.headerImage}
                alt="About header"
                className="w-full h-24 object-cover"
              />
            )}
            <div className="p-4">
              <h2 className="text-lg font-bold text-text-primary mb-3">About</h2>
              <p className="text-sm text-text-secondary leading-relaxed mb-4">
                {artistData?.about?.aboutText?.substring(0, 200) || 
                "Sarah Mitchell is a contemporary visual artist whose work explores the intersection of digital technology and traditional artistic mediums..."}
              </p>
              
              <div className="bg-muted p-3 rounded-lg">
                <h3 className="text-sm font-medium text-text-primary mb-2">Booking Contact</h3>
                <p className="text-xs text-text-secondary">
                  {artistData?.about?.bookingContact?.name || "Maria Rodriguez"}
                </p>
                <p className="text-xs text-accent">
                  {artistData?.about?.bookingContact?.email || "maria@sarahmitchellart.com"}
                </p>
              </div>
            </div>
          </div>
        );
      
      case 'calendar':
        return (
          <div className="w-full h-full bg-surface overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg font-bold text-text-primary mb-4">Events</h2>
              <div className="space-y-3">
                {[1, 2, 3]?.map((i) => (
                  <div key={i} className="flex items-center space-x-3 p-3 bg-muted rounded-lg">
                    <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                      <Icon name="Calendar" size={16} color="white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">Summer Concert {i}</p>
                      <p className="text-xs text-text-secondary">July {15 + i}, 2024</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'work':
        return (
          <div className="w-full h-full bg-surface overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg font-bold text-text-primary mb-4">Portfolio</h2>
              <div className="grid grid-cols-2 gap-2">
                {[1, 2, 3, 4]?.map((i) => (
                  <div key={i} className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="Image" size={24} className="text-text-secondary" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'shop':
        return (
          <div className="w-full h-full bg-surface overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg font-bold text-text-primary mb-4">Shop</h2>
              <div className="grid grid-cols-2 gap-3">
                {['T-Shirts', 'Prints', 'Posters', 'Bags']?.map((item) => (
                  <div key={item} className="bg-muted p-3 rounded-lg text-center">
                    <Icon name="ShoppingBag" size={20} className="text-accent mx-auto mb-2" />
                    <p className="text-xs font-medium text-text-primary">{item}</p>
                    <p className="text-xs text-text-secondary">Available</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-surface rounded-lg border border-border p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <Icon name="Smartphone" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Mobile App Preview</h2>
              <p className="text-sm text-text-secondary">Preview how your content appears in the mobile app</p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Screen Navigation */}
          <div className="w-48 space-y-2">
            <h3 className="text-sm font-medium text-text-primary mb-3">App Screens</h3>
            {screens?.map((screen) => (
              <Button
                key={screen?.id}
                variant={currentScreen === screen?.id ? "default" : "ghost"}
                size="sm"
                fullWidth
                onClick={() => setCurrentScreen(screen?.id)}
                iconName={screen?.icon}
                iconPosition="left"
                iconSize={16}
                className="justify-start"
              >
                {screen?.name}
              </Button>
            ))}
          </div>

          {/* Mobile Preview */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-80 h-[600px] bg-gray-900 rounded-[2.5rem] p-2">
                <div className="w-full h-full bg-black rounded-[2rem] overflow-hidden relative">
                  {/* Status Bar */}
                  <div className="absolute top-0 left-0 right-0 h-8 bg-black/20 flex items-center justify-between px-6 text-white text-xs z-10">
                    <span>9:41</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Signal" size={12} />
                      <Icon name="Wifi" size={12} />
                      <Icon name="Battery" size={12} />
                    </div>
                  </div>
                  
                  {/* Screen Content */}
                  <div className="pt-8 h-full">
                    {renderScreen()}
                  </div>
                  
                  {/* Navigation Bar (for non-splash screens) */}
                  {currentScreen !== 'splash' && (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-surface border-t border-border flex items-center justify-around">
                      {screens?.slice(1)?.map((screen) => (
                        <button
                          key={screen?.id}
                          onClick={() => setCurrentScreen(screen?.id)}
                          className={`flex flex-col items-center space-y-1 p-2 ${
                            currentScreen === screen?.id ? 'text-accent' : 'text-text-secondary'
                          }`}
                        >
                          <Icon name={screen?.icon} size={16} />
                          <span className="text-xs">{screen?.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Info */}
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-accent mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-text-primary mb-1">Preview Information</h4>
              <p className="text-sm text-text-secondary">
                This preview shows how your content will appear in the mobile app. 
                Some features may appear differently in the actual app due to device variations and real-time data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePreviewModal;